import { css, keyframes } from "@emotion/react";

const SPRING = "cubic-bezier(0.34, 1.42, 0.5, 1)";
const EASE_OUT = "cubic-bezier(0.55, 0, 0.5, 1)";
const EASE_IN_OUT = "cubic-bezier(0.4, 0, 0.2, 1)";

const Z_INDEX_BACKDROP = 1000;
const Z_INDEX_CARD = 1001;

const pulseGlow = keyframes({
  "0%, 100%": { transform: "scale(1)", opacity: 0 },
  "50%": { transform: "scale(1.09)", opacity: 0.65 },
});

export const backdropStyle = css({
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0)",
  pointerEvents: "none",
  transition: `background 380ms ${EASE_IN_OUT}`,
  zIndex: Z_INDEX_BACKDROP,
  '&[data-state="modal"]': {
    background: "rgba(0, 0, 0, 0.62)",
    pointerEvents: "auto",
  },
});

export const cardStyle = css({
  position: "fixed",
  zIndex: Z_INDEX_CARD,
  color: "#fff",
  cursor: "pointer",
  boxSizing: "border-box",
  willChange: "top, left, width, height",
  transition: [
    `top 520ms ${SPRING}`,
    `left 520ms ${SPRING}`,
    `width 520ms ${SPRING}`,
    `height 520ms ${SPRING}`,
    `border-radius 460ms ${EASE_IN_OUT}`,
    "background-color 380ms ease",
  ].join(", "),
  '&[data-state="modal"]': {
    cursor: "default",
  },
  '&[data-state="closing"]': {
    transition: [
      `top 420ms ${EASE_OUT}`,
      `left 420ms ${EASE_OUT}`,
      `width 420ms ${EASE_OUT}`,
      `height 420ms ${EASE_OUT}`,
      `border-radius 420ms ${EASE_OUT}`,
      "background-color 380ms ease",
    ].join(", "),
  },
});

export const ringStyle = css({
  position: "absolute",
  inset: -3,
  borderRadius: 32,
  border: "1.5px solid #00c2ae",
  opacity: 0,
  pointerEvents: "none",
  '&[data-state="fab"]': {
    animation: `${pulseGlow} 2.6s ease-in-out infinite`,
  },
});

export const fabLayerStyle = css({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  gap: 11,
  padding: "0 16px",
  transition: `opacity 220ms ease-out, transform 380ms ${SPRING}`,
  '&[data-state="fab"]': {
    opacity: 1,
    transform: "scale(1)",
    transitionDelay: "220ms, 240ms",
  },
  '&[data-state="modal"]': {
    opacity: 0,
    transform: "scale(0.88)",
    pointerEvents: "none",
  },
  '&[data-state="closing"]': {
    opacity: 1,
    transform: "scale(1)",
    transitionDelay: "240ms, 260ms",
  },
});

export const fabIconStyle = css({
  width: 34,
  height: 34,
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.22)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const fabTextStyle = css({
  lineHeight: 1.25,
  color: "#fff",
  textAlign: "left",
  minWidth: 0,
  overflow: "hidden",
});

export const fabText1Style = css({
  fontSize: 12,
  opacity: 0.9,
  fontWeight: 700,
});

export const fabText2Style = css({
  fontSize: 12,
  fontWeight: 400,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const modalLayerStyle = css({
  position: "absolute",
  inset: 0,
  padding: "12px 20px",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  color: "#1a1a1a",
  overflowY: "auto",
  overscrollBehavior: "contain",
  WebkitOverflowScrolling: "touch",
  transition: `opacity 220ms ease-out, transform 380ms ${SPRING}`,
  '&[data-state="modal"]': {
    opacity: 1,
    transform: "scale(1)",
    transitionDelay: "180ms, 140ms",
  },
  '&[data-state="fab"]': {
    opacity: 0,
    transform: "scale(0.94)",
    pointerEvents: "none",
  },
  '&[data-state="closing"]': {
    opacity: 0,
    transform: "scale(0.94)",
    pointerEvents: "none",
  },
});

export const modalHeaderStyle = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0,
  marginBottom: 16,
});

export const modalCloseButtonStyle = css({
  position: "absolute",
  top: 0,
  right: 0,
  width: 36,
  height: 36,
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#1a1a1a",
  "&:hover": {
    opacity: 0.65,
  },
});

export const modalHeaderRowStyle = css({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  gap: 8,
});

export const modalHeaderIconStyle = css({
  color: "#00c2ae",
});

export const modalHeaderTitleStyle = css({
  display: "flex",
  alignItems: "center",
  fontSize: 16,
  fontWeight: 700,
  color: "#1a1a1a",
  letterSpacing: 0.5,
});

export const modalHeaderTitleIndexStyle = css({
  display: "inline-flex",
  alignItems: "center",
  verticalAlign: "middle",
  fontSize: 12,
  fontWeight: 700,
  color: "#1a1a1a",
  marginLeft: 4,
});

export const thumbnailContainerStyle = css({
  width: "100%",
  marginBottom: 16,
  "@media (max-height: 599px)": {
    display: "none",
  },
});

export const thumbnailWrapStyle = css({
  height: 162,
  width: "100%",
  background: "#eef0f3",
  overflow: "hidden",
});

export const thumbnailImgStyle = css({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

export const detailRowStyle = css({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "4px 0",
  fontSize: 14,
  color: "#1a1a1a",
});

export const detailIconStyle = css({
  color: "#1a1a1a",
  fontSize: 20,
  flexShrink: 0,
});

export const detailTextStyle = css({
  lineHeight: 1.5,
  whiteSpace: "pre-line",
  fontWeight: 600,
});

export const modalCtaStyle = css({
  width: "100%",
  padding: 14,
  marginTop: 16,
  background: "#00c2ae",
  color: "#ffffff",
  border: "none",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  boxSizing: "border-box",
  "&:hover": {
    background: "#1fd6c4",
  },
});

const sideButtonStyle = {
  width: 28,
  height: 28,
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "&:hover svg rect": {
    fillOpacity: 0.65,
  },
};

export const prevButtonStyle = css({
  ...sideButtonStyle,
  transform: "scaleX(-1)",
});

export const nextButtonStyle = css(sideButtonStyle);
